import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MantineProvider, createTheme, ColorSchemeScript, localStorageColorSchemeManager } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
// import { GoogleOAuthProvider } from '@react-oauth/google'; // REMOVIDO TEMPORALMENTE
import SessionTimeoutProvider from './components/SessionTimeoutProvider';
import { getSessionConfig } from './config/sessionConfig';
import LandingPage from './pages/LandingPage';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import ExamLayout from './components/ExamLayout';
import AdminDashboard from './pages/AdminDashboard';
import MejorarPlanPage from './pages/MejorarPlanPage';
import CheckoutPage from './pages/CheckoutPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentFailurePage from './pages/PaymentFailurePage';
import EstudianteDashboard from './pages/EstudianteDashboard';
import SettingsPage from './pages/SettingsPage';
import PerfilPublicoPage from './pages/PerfilPublicoPage';
import RegisterPage from './pages/RegisterPage';
// Páginas del módulo estudiantil
import SimuladorPage from './pages/SimuladorPage';
import ExamenRapidoPage from './pages/ExamenRapidoPage';
import ExamenFiltrosPage from './pages/ExamenFiltrosPage';
import ExamenFiltradoPage from './pages/ExamenFiltradoPage';
import ExamenSimulacroPage from './pages/ExamenSimulacroPage';
import RepasoInteligentePage from './pages/RepasoInteligentePage';
import EstadisticasPage from './pages/EstadisticasPage';
import HistorialCompletoPage from './pages/HistorialCompletoPage';
import RankingsPage from './pages/RankingsPage';
import CrearPreguntasPage from './pages/CrearPreguntasPage';
import NotificacionesPage from './pages/NotificacionesPage';
import SolicitudesPage from './pages/SolicitudesPage';
// Páginas de flashcards
import FlashcardsPage from './pages/FlashcardsPage';
import EstudiarFlashcardsPage from './pages/EstudiarFlashcardsPage';
import FlashcardsResumenPage from './pages/FlashcardsResumenPage';
import FlashcardsEstadisticasPage from './pages/FlashcardsEstadisticasPage';
// Páginas del módulo de administración
import ClinicalCasesPage from './pages/admin/ClinicalCasesPage';
import QuestionReviewsPage from './pages/admin/QuestionReviewsPage';
import UserStatisticsPage from './pages/admin/UserStatisticsPage';
import SubscriptionsPage from './pages/admin/SubscriptionsPage';
import QuestionDatabasePage from './pages/admin/QuestionDatabasePage';
import AdminNotificationsPage from './pages/admin/AdminNotificationsPage';
import PermissionsAdminPage from './pages/admin/PermissionsAdminPage';
import AdminFlashcardsPage from './pages/admin/AdminFlashcardsPage';
import AdminVideosPage from './pages/admin/AdminVideosPage';
import AdminMaterialesPage from './pages/admin/AdminMaterialesPage';
import MaterialesPage from './pages/MaterialesPage';
import ReferenciaEnarmAdminPage from './pages/admin/ReferenciaEnarmAdminPage';
import UsersPage from './pages/admin/UsersPage';
import FinancesPage from './pages/admin/FinancesPage';
import QuejasSugerenciasPage from './pages/admin/QuejasSugerenciasPage';
import VideosEstudioPage from './pages/VideosEstudioPage';
import ResultadosPage from './pages/ResultadosPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Páginas multijugador
import MultijugadorPage from './pages/MultijugadorPage';
import SalaMultijugadorPage from './pages/SalaMultijugadorPage';
import ExamenMultijugadorPage from './pages/ExamenMultijugadorPage';

//Páginas de examen
// import ExamenPage from './pages/ExamenPage';

import './App.css';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import './styles/mantine-custom.css';

// Importar servicios para configurar interceptors globales
import './services';

const theme = createTheme({
  primaryColor: 'blue',
  colors: {
    'medical-blue': [
      '#f0f9ff',
      '#e0f2fe',
      '#bae6fd',
      '#7dd3fc',
      '#38bdf8',
      '#0ea5e9',
      '#0284c7',
      '#0369a1',
      '#075985',
      '#0c4a6e'
    ],
    'medical-green': [
      '#f0fdf4',
      '#dcfce7',
      '#bbf7d0',
      '#86efac',
      '#4ade80',
      '#22c55e',
      '#16a34a',
      '#15803d',
      '#166534',
      '#14532d'
    ]
  },
  fontFamily: 'Inter, sans-serif',
  headings: { fontFamily: 'Inter, sans-serif' },
  radius: {
    md: '12px',
    lg: '16px',
    xl: '24px'
  },
  other: {
    lightBg: '#f7f3ee',
    lightCard: '#ebe8e3',
    lightBorder: '#ddd8d1',
    lightText: '#2d2a26',
    lightTextSecondary: '#5a5550'
  }
});

const colorSchemeManager = localStorageColorSchemeManager({
  key: 'enarm360-color-scheme',
});

function App() {
  // Obtener configuración de sesión según el entorno
  const sessionConfig = getSessionConfig();

  return (
    <>
      <ColorSchemeScript defaultColorScheme="light" />
      <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme} colorSchemeManager={colorSchemeManager} defaultColorScheme="light" forceColorScheme="light">
        <Notifications position="top-right" autoClose={2500} />
        <Router>
          <SessionTimeoutProvider 
            timeout={sessionConfig.timeout}
            warningTime={sessionConfig.warningTime}
            enabled={sessionConfig.enabled}
          >
          <div className="App">
            <Routes>
              {/* Rutas públicas */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Rutas protegidas con Layout */}
              <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                {/* Rutas de administrador */}
                <Route path="/admin/dashboard" element={<ProtectedRoute requiredRole="ADMIN"><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/clinical-cases" element={<ProtectedRoute requiredRole="ADMIN"><ClinicalCasesPage /></ProtectedRoute>} />
                <Route path="/admin/question-reviews" element={<ProtectedRoute requiredRole="ADMIN"><QuestionReviewsPage /></ProtectedRoute>} />
                <Route path="/admin/statistics" element={<ProtectedRoute requiredRole="ADMIN"><UserStatisticsPage /></ProtectedRoute>} />
                <Route path="/admin/subscriptions" element={<ProtectedRoute requiredRole="ADMIN"><SubscriptionsPage /></ProtectedRoute>} />
                <Route path="/admin/question-database" element={<ProtectedRoute requiredRole="ADMIN"><QuestionDatabasePage /></ProtectedRoute>} />
                <Route path="/admin/permissions" element={<ProtectedRoute requiredRole="ADMIN"><PermissionsAdminPage /></ProtectedRoute>} />
                <Route path="/admin/flashcards" element={<ProtectedRoute requiredRole="ADMIN"><AdminFlashcardsPage /></ProtectedRoute>} />
                <Route path="/admin/usuarios" element={<ProtectedRoute requiredRole="ADMIN"><UsersPage /></ProtectedRoute>} />
                <Route path="/admin/notificaciones" element={<ProtectedRoute requiredRole="ADMIN"><AdminNotificationsPage /></ProtectedRoute>} />
                <Route path="/admin/finanzas" element={<ProtectedRoute requiredRole="ADMIN"><FinancesPage /></ProtectedRoute>} />
                <Route path="/admin/quejas" element={<ProtectedRoute requiredRole="ADMIN"><QuejasSugerenciasPage /></ProtectedRoute>} />
                <Route path="/admin/videos" element={<ProtectedRoute requiredRole="ADMIN"><AdminVideosPage /></ProtectedRoute>} />
                <Route path="/admin/materiales" element={<ProtectedRoute requiredRole="ADMIN"><AdminMaterialesPage /></ProtectedRoute>} />
                <Route path="/admin/referencia-enarm" element={<ProtectedRoute requiredRole="ADMIN"><ReferenciaEnarmAdminPage /></ProtectedRoute>} />

                {/* Rutas de estudiante */}
                <Route path="/estudiante/dashboard" element={<ProtectedRoute requiredRole="ESTUDIANTE"><EstudianteDashboard /></ProtectedRoute>} />
                <Route path="/estudiante/simulador" element={<ProtectedRoute requiredRole="ESTUDIANTE"><SimuladorPage /></ProtectedRoute>} />
                <Route path="/estudiante/simulador/rapido" element={<ProtectedRoute requiredRole="ESTUDIANTE"><ExamenRapidoPage /></ProtectedRoute>} />
                <Route path="/estudiante/simulador/filtros" element={<ProtectedRoute requiredRole="ESTUDIANTE"><ExamenFiltrosPage /></ProtectedRoute>} />
                <Route path="/estudiante/examen-filtrado/:intentoId" element={<ProtectedRoute requiredRole="ESTUDIANTE"><ExamenFiltradoPage /></ProtectedRoute>} />
                <Route path="/estudiante/simulador/completo" element={<ProtectedRoute requiredRole="ESTUDIANTE"><ExamenSimulacroPage /></ProtectedRoute>} />
                <Route path="/estudiante/simulador/inteligente" element={<ProtectedRoute requiredRole="ESTUDIANTE"><RepasoInteligentePage /></ProtectedRoute>} />
                <Route path="/estudiante/estadisticas" element={<ProtectedRoute requiredRole="ESTUDIANTE"><EstadisticasPage /></ProtectedRoute>} />
                <Route path="/estudiante/historial-completo" element={<ProtectedRoute requiredRole="ESTUDIANTE"><HistorialCompletoPage /></ProtectedRoute>} />
                <Route path="/estudiante/rankings" element={<ProtectedRoute requiredRole="ESTUDIANTE"><RankingsPage /></ProtectedRoute>} />
                <Route path="/estudiante/preguntas" element={<ProtectedRoute requiredRole="ESTUDIANTE"><CrearPreguntasPage /></ProtectedRoute>} />
                <Route path="/estudiante/notificaciones" element={<ProtectedRoute requiredRole="ESTUDIANTE"><NotificacionesPage /></ProtectedRoute>} />
                <Route path="/estudiante/solicitudes" element={<ProtectedRoute requiredRole="ESTUDIANTE"><SolicitudesPage /></ProtectedRoute>} />
                <Route path="/estudiante/flashcards" element={<ProtectedRoute requiredRole="ESTUDIANTE"><FlashcardsPage /></ProtectedRoute>} />
                <Route path="/estudiante/flashcards/estudiar" element={<ProtectedRoute requiredRole="ESTUDIANTE"><EstudiarFlashcardsPage /></ProtectedRoute>} />
                <Route path="/estudiante/flashcards/resumen" element={<ProtectedRoute requiredRole="ESTUDIANTE"><FlashcardsResumenPage /></ProtectedRoute>} />
                <Route path="/estudiante/flashcards/estadisticas" element={<ProtectedRoute requiredRole="ESTUDIANTE"><FlashcardsEstadisticasPage /></ProtectedRoute>} />
                <Route path="/estudiante/videos" element={<ProtectedRoute requiredRole="ESTUDIANTE"><VideosEstudioPage /></ProtectedRoute>} />
                <Route path="/estudiante/materiales" element={<ProtectedRoute requiredRole="ESTUDIANTE"><MaterialesPage /></ProtectedRoute>} />

                {/* Rutas multijugador */}
                <Route path="/multijugador" element={<ProtectedRoute requiredRole="ESTUDIANTE"><MultijugadorPage /></ProtectedRoute>} />
                <Route path="/multijugador/sala/:codigoSala" element={<ProtectedRoute requiredRole="ESTUDIANTE"><SalaMultijugadorPage /></ProtectedRoute>} />
                <Route path="/multijugador/sala/:codigoSala/ranking" element={<ProtectedRoute requiredRole="ESTUDIANTE"><SalaMultijugadorPage /></ProtectedRoute>} />

                {/* Perfil público de cualquier usuario */}
                <Route path="/perfil/:userId" element={<ProtectedRoute><PerfilPublicoPage /></ProtectedRoute>} />

                {/* Configuración */}
                <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                <Route path="/settings/:section" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

                {/* Rutas de resultados con sidebar colapsado */}
                <Route path="/examenes/:intentoId/resultado" element={<ProtectedRoute requiredRole="ESTUDIANTE"><ResultadosPage /></ProtectedRoute>} />
                <Route path="/estudiante/resultados/:intentoId" element={<ProtectedRoute requiredRole="ESTUDIANTE"><ResultadosPage /></ProtectedRoute>} />

                {/* Redirección */}
                <Route path="/profile" element={<Navigate to="/settings" replace />} />
              </Route>

              {/* Rutas de examen con layout sin distracciones */}
              <Route element={<ProtectedRoute><ExamLayout /></ProtectedRoute>}>
                {/* <Route path="/examen/:id" element={<ProtectedRoute requiredRole="ESTUDIANTE"><ExamenPage /></ProtectedRoute>} /> */}
                <Route path="/examen/multijugador" element={<ProtectedRoute requiredRole="ESTUDIANTE"><ExamenMultijugadorPage /></ProtectedRoute>} />
              </Route>

              {/* Mejorar plan y checkout (sin sidebar / header) */}
              <Route path="/mejorarplan" element={<ProtectedRoute requiredRole="ESTUDIANTE"><MejorarPlanPage /></ProtectedRoute>} />
              <Route path="/checkout/:planId" element={<ProtectedRoute requiredRole="ESTUDIANTE"><CheckoutPage /></ProtectedRoute>} />
              
              {/* Rutas de resultado de pago MercadoPago */}
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/payment-failure" element={<PaymentFailurePage />} />
              <Route path="/payment-pending" element={<PaymentSuccessPage />} />

              {/* Ruta por defecto - redirige a home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          </SessionTimeoutProvider>
        </Router>
      </MantineProvider>
      </QueryClientProvider>
    </>
  );
}

export default App;
